/**
 * TeamRow — one conversation in the Team list (staff chat MVP, SR-14), ported from Antonin's catalog source
 * (Signal DS `catalog/src/components/TeamChat/TeamChat.tsx`, `TeamRow`): the colleague's initials, name and time, then
 * the last message and the unread count, then their role. One-to-one only: no group glyph, no "N people", no sender
 * name before a preview. With the patient switch (?x=staff-chat-patient) a patient thread names its patient instead
 * of the role.
 */
import { Avatar } from '@scripsteam/scrips-signal-ds';
import { listTime, previewLine, threadPatientLine, threadRole, threadTitle } from '@/staff-chat/lib/chat-format';
import { isStaffChatPatientOn } from '@/staff-chat/lib/staff-chat-switch';
import type { StaffChatThread } from '@/staff-chat/types/staff-chat';
import { TeamCountBadge } from './team-count-badge';

export interface TeamRowProps {
  thread: StaffChatThread;
  me: string | undefined;
  onClick?: () => void;
  selected?: boolean;
}

export function TeamRow({ thread: t, me, onClick, selected }: TeamRowProps) {
  const title = threadTitle(t, me);
  const role = (isStaffChatPatientOn() ? threadPatientLine(t) : null) ?? threadRole(t, me);
  const unread = t.unreadCount > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className="team-chat-row"
      data-thread-id={t.id}
      data-unread={unread}
      data-selected={selected || undefined}
      aria-current={selected || undefined}
      aria-label={`${title}${unread ? `, ${t.unreadCount} unread` : ''}`}
    >
      <Avatar scale="compact" tone="provider" name={title} size={32} />
      <span className="team-chat-body">
        <span className="team-chat-line">
          <span className="team-chat-title">{title}</span>
          <span className="team-chat-meta">{listTime(t.lastMessage?.createdAt ?? t.updatedAt)}</span>
        </span>
        <span className="team-chat-line">
          <span className="team-chat-preview" dir="auto">
            {previewLine(t, me)}
          </span>
          <TeamCountBadge count={t.unreadCount} />
        </span>
        {role ? <span className="team-chat-meta">{role}</span> : null}
      </span>
    </button>
  );
}
